import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { CalendarIcon, X, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import { useState, useMemo } from "react";

interface CouplesPackFiltersProps {
  usage_id?: string;
}

export function CouplesPackFilters({ usage_id }: CouplesPackFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current filter values from URL
  const dateRangeStart = searchParams.get("start_date");
  const dateRangeEnd = searchParams.get("end_date");
  const status = searchParams.get("status") || "all";
  const professionalId = searchParams.get("professional_id") || "ALL";

  const { data: professionalsData, isLoading: professionalsLoading } =
    useAllProfessionals();
  const [professionalSearch, setProfessionalSearch] = useState("");

  const professionals = useMemo(() => {
    const allProfessionals = professionalsData?.professionals || [];
    const sorted = [...allProfessionals].sort((a, b) => 
      a.name.localeCompare(b.name)
    );
    
    if (!professionalSearch.trim()) {
      return sorted;
    }
    
    const searchLower = professionalSearch.toLowerCase();
    return sorted.filter((prof) =>
      prof.name.toLowerCase().includes(searchLower)
    );
  }, [professionalsData?.professionals, professionalSearch]);

  const form = useForm({
    defaultValues: {
      dateRange: {
        start: dateRangeStart ? new Date(dateRangeStart) : null,
        end: dateRangeEnd ? new Date(dateRangeEnd) : null,
      },
      status,
      professionalId,
    },
  });

  // Update URL params when filters change
  const handleFilterChange = (updates: Record<string, any>) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", "0");

    Object.entries(updates).forEach(([key, value]) => {
      if (key === "dateRange") {
        // Only update URL if both dates are selected
        if (value.start && value.end) {
          newParams.set("start_date", value.start.toISOString());
          // Set end_date to EOD (23:59:59) in ISO string
          const endDate = new Date(value.end);
          endDate.setHours(23, 59, 59, 999);
          newParams.set("end_date", endDate.toISOString());
        } else {
          // Clear dates if not both selected
          newParams.delete("start_date");
          newParams.delete("end_date");
        }
      } else if (value && value !== "ALL" && value !== "all") {
        newParams.set(key === "professionalId" ? "professional_id" : key, value);
      } else {
        newParams.delete(key === "professionalId" ? "professional_id" : key);
      }
    });

    if (usage_id) newParams.set("usage_id", usage_id);
    setSearchParams(newParams);
  };

  const handleReset = () => {
    const newParams = new URLSearchParams();
    newParams.set("page", "0");
    // Keep search if it exists
    const search = searchParams.get("search");
    if (search) newParams.set("search", search);
    if (usage_id) newParams.set("usage_id", usage_id);
    setSearchParams(newParams);
    
    form.reset({
      dateRange: { start: null, end: null },
      status: "all",
      professionalId: "ALL",
    });
  };

  const hasFilters =
    dateRangeStart ||
    dateRangeEnd ||
    (status && status !== "all") ||
    (professionalId && professionalId !== "ALL");

  return (
    <Form {...form}>
      <div className="flex flex-wrap items-center gap-4">
        {/* Date Range */}
        <FormField
          control={form.control}
          name="dateRange"
          render={({ field }) => (
            <FormItem>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={`w-[240px] justify-start text-left font-normal ${
                        !field.value.start && "text-muted-foreground"
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value.start && field.value.end ? (
                        <>
                          {format(field.value.start, "LLL dd, y")} -{" "}
                          {format(field.value.end, "LLL dd, y")}
                        </>
                      ) : (
                        <span>Pick a date range</span>
                      )}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  {/* Two input boxes for start and end dates */}
                  <div className="flex items-end gap-3 mb-4 px-4">
                    {/* Start Date Box */}
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1.5 block">
                        Start
                      </label>
                      <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                        {field.value.start ? (
                          format(field.value.start, "LLL dd, y")
                        ) : (
                          <span className="text-muted-foreground">Select</span>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />

                    {/* End Date Box */}
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1.5 block">
                        End
                      </label>
                      <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                        {field.value.end ? (
                          format(field.value.end, "LLL dd, y")
                        ) : (
                          <span className="text-muted-foreground">Select</span>
                        )}
                      </div>
                    </div>

                    {/* Clear button - X icon after end box */}
                    {(field.value.start || field.value.end) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 mb-0 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          const newValue = {
                            start: null,
                            end: null,
                          };
                          field.onChange(newValue);
                          handleFilterChange({ dateRange: newValue });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Calendar below the boxes */}
                  <Calendar
                    initialFocus
                    mode="range"
                    selected={{
                      from: field.value.start || undefined,
                      to: field.value.end || undefined,
                    }}
                    onSelect={(range) => {
                      const newValue = {
                        start: range?.from || null,
                        end: range?.to || null,
                      };
                      field.onChange(newValue);
                      // Only trigger API call if both dates are selected
                      if (range?.from && range?.to) {
                        handleFilterChange({ dateRange: newValue });
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </FormItem>
          )}
        />

        {/* Status */}
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleFilterChange({ status: value });
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        {/* Professional */}
        <FormField
          control={form.control}
          name="professionalId"
          render={({ field }) => (
            <FormItem className="flex-1 min-w-[200px]">
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleFilterChange({ professionalId: value });
                  setProfessionalSearch(""); // Clear search on selection
                }}
                disabled={professionalsLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select professional" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search professionals..."
                        value={professionalSearch}
                        onChange={(e) => setProfessionalSearch(e.target.value)}
                        className="pl-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    <SelectItem value="ALL">All Professionals</SelectItem>
                    {professionals.length > 0 ? (
                      professionals.map((professional) => (
                        <SelectItem key={professional.id} value={professional.id}>
                          {professional.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No professionals found
                      </div>
                    )}
                  </div>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        {/* Reset Button */}
        {hasFilters && (
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="ml-auto"
          >
            <X className="h-4 w-4 mr-1" />
            Reset Filters
          </Button>
        )}
      </div>
    </Form>
  );
}

